import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "@style/Admin.scss";
import RightTimeApi from "@/API";
import Enums from "@/enums";
import { FaPlusSquare } from "react-icons/fa";
import { toast } from "react-toastify";
import { MdCheckBoxOutlineBlank, MdCheckBox } from "react-icons/md";
import { IoTrashOutline } from "react-icons/io5";
import ConfirmModal from "@modals/ConfirmModal";
import StringInputModal from "@elements/modals/StringInputModal";
import AddCustomerWizardModal from "../elements/modals/AddCustomerWizardModal";

type CompanyListItem = {
  id: number;
  name?: string;
  alias?: string;
  active?: boolean;
};

export default function AdminCompaniesTab(props: any) {
  const { updateSpinner, activeTab, adminPerms } = props.tabData;
  const [companies, updateCompanies] = useState<CompanyListItem[] | null>(null);
  const [showAddCustomerWizard, setShowAddCustomerWizard] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showDeletePasswordModal, setShowDeletePasswordModal] = useState(false);
  const [companyToDelete, setCompanyToDelete] = useState<CompanyListItem | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  let navigate = useNavigate();

  const isProdEnvironment = (import.meta.env.VITE_ENV || "PROD") === "PROD";
  const canShowDeleteColumn =
    !isProdEnvironment &&
    Array.isArray(adminPerms) &&
    adminPerms.includes(Enums.ROLE_PERMS.SystemAdmin);

  // console.log("AdminCompaniesTab rendered with activeTab:", activeTab);
  useEffect(() => {
    console.log("AdminCompaniesTab activeTab changed:", activeTab);
    if (isOnThisTab() && !companies) {
      updateCompanies([]);
      getCompaniesAsync();
    }
  }, [activeTab]);

  function isOnThisTab() {
    return activeTab && activeTab === "companies";
  }

  // Compute listedCompanies in main body
  let listedCompanies = [];
  if (companies && companies.length > 0) {
    if (showAll)
      listedCompanies = companies;
    else
      listedCompanies = companies.filter(c => c.active);
  }

  if (!(isOnThisTab() && listedCompanies && listedCompanies.length > 0)) {
    return null;
  }



  async function getCompaniesAsync() {
    const resp = await RightTimeApi.getAsync("/CompaniesList");
    if (resp && resp.ok && resp.jsonData) {
      updateCompanies(resp.jsonData);
    }
  }

  async function toggleActiveAsync(e) {
    e.stopPropagation();
    updateSpinner && updateSpinner(true);
    const companyId = e.currentTarget.dataset.id;
    const url = `/Company/${companyId}/ToggleActive`;
    var resp = await RightTimeApi.putAsync(url);
    if (resp && resp.ok) {
      await getCompaniesAsync();
      const isActive = resp?.jsonData?.active;
      const company = companies?.find((c) => c.id === parseInt(companyId));
      const companyName = company?.alias || company?.name || "Company";
      toast.success(`Company "${companyName}" ${isActive ? "activated" : "deactivated"}.`);
      updateSpinner && updateSpinner(false);
    }
  }

  async function onCustomerCreatedAsync(companyId: number) {
    await getCompaniesAsync();
    navigate("/Company/" + companyId);
  }

  function onDeleteClick(e, company: CompanyListItem) {
    e.stopPropagation();
    if (isDeleting) return;
    setCompanyToDelete(company);
    setShowDeleteConfirm(true);
  }

  function handleDeleteConfirmed() {
    setShowDeleteConfirm(false);
    if (isProdEnvironment) {
      setShowDeletePasswordModal(true);
      return;
    }
    void onDeleteCompanyAsync();
  }

  function handleDeletePasswordCancel() {
    setShowDeletePasswordModal(false);
    setCompanyToDelete(null);
  }

  function handleDeletePasswordSubmit(adminPassword: string) {
    void onDeleteCompanyAsync(adminPassword);
  }

  async function onDeleteCompanyAsync(adminPassword?: string) {
    if (!companyToDelete?.id || isDeleting) return;
    setIsDeleting(true);
    updateSpinner && updateSpinner(true);
    let keepPasswordModalOpen = false;
    try {
      const payload = adminPassword ? { adminPassword } : undefined;
      const resp = await RightTimeApi.deleteAsync(`/Company/${companyToDelete.id}`, payload);
      if (resp && resp.ok) {
        toast.success("Company deleted.");
        setCompanyToDelete(null);
        await getCompaniesAsync();
        return;
      }
      if (resp?.status === 403 && isProdEnvironment) {
        toast.error("Invalid admin password.");
        keepPasswordModalOpen = true;
        return;
      }
      toast.error("Unable to delete company.");
    } finally {
      updateSpinner && updateSpinner(false);
      setIsDeleting(false);
      setShowDeleteConfirm(false);
      if (!keepPasswordModalOpen) {
        setShowDeletePasswordModal(false);
      }
    }
  }

  function companyDisplayName(company: CompanyListItem) {
    return company.alias || company.name || "this company";
  }

  return (
    <>
      <div className="tableWrapper dimensionTable companiesTable scrollable rowClickable">
        <table>
          <caption>
            <span className="title">Companies</span>
            {adminPerms && adminPerms.includes(254) && (
              <span className="captionRight">
                  <FaPlusSquare
                    className="actionIcon addCompany"
                    title="Add Company"
                    onClick={() => setShowAddCustomerWizard(true)}
                  />
              </span>
            )}
          </caption>
          <thead>
            <tr>
              <th>Id</th>
              <th>
                Name
              </th>
              <th className="toggleColumnHeader clickable" data-help-id="ShowSelectedOrAll" onClick={() => setShowAll(!showAll)}>{showAll ? "All" : "Active"}</th>
              {canShowDeleteColumn ? <th></th> : null}
            </tr>
          </thead>
          <tbody>
            {listedCompanies.map((c, i) => (
              <tr key={"company" + c.id}>
                <td>{c.id}</td>
                <td
                  onClick={() => navigate("/Company/" + c.id)}
                >
                  {c.alias || c.name}
                </td>
                <td onClick={toggleActiveAsync} data-id={c.id}>
                  {c.active ? (
                    <MdCheckBox className="actionIcon" />
                  ) : (
                    <MdCheckBoxOutlineBlank className="actionIcon" />
                  )}
                </td>
                {canShowDeleteColumn ? (
                  <td className="actions">
                    <IoTrashOutline
                      className="actionIcon deleteIcon"
                      onClick={(e) => onDeleteClick(e, c)}
                      title={isDeleting && companyToDelete?.id === c.id ? "Deleting..." : "Delete Company"}
                    />
                  </td>
                ) : null}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <AddCustomerWizardModal
        isOpen={showAddCustomerWizard}
        onClose={() => setShowAddCustomerWizard(false)}
        onCreated={onCustomerCreatedAsync}
        updateSpinner={updateSpinner}
      />

      <ConfirmModal
        isOpen={showDeleteConfirm}
        onConfirm={handleDeleteConfirmed}
        onCancel={() => {
          setShowDeleteConfirm(false);
          setCompanyToDelete(null);
        }}
        title="Delete Company"
        message={`Are you sure you want to delete company "${companyToDelete ? companyDisplayName(companyToDelete) : "this company"}"?`}
        confirmText="Yes, Delete Company"
        dangerText="This will permanently remove the company and all associated data."
        showSecondStep={true}
        secondStepMessage={`Delete ${companyToDelete ? companyDisplayName(companyToDelete) : "this company"} permanently?`}
        secondStepWarning="This company will be permanently removed from the system."
        secondStepDetail="This includes users, sites, tests, statuses, roles, and related configuration. This action cannot be undone."
      />

      <StringInputModal
        isOpen={showDeletePasswordModal}
        onClose={handleDeletePasswordCancel}
        onSubmit={handleDeletePasswordSubmit}
        title="Confirm Admin Password"
        label="Enter the universal admin password to delete this company in production:"
        buttonText="Delete Company"
        placeholder="Admin password"
        required={true}
        inputType="password"
      />
    </>
  );
}
